import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { forgotPassword } from "@/lib/apiClient";
import { useI18n } from "@/contexts/I18nContext";
import { Mail, ArrowRight, CheckCircle2 } from "lucide-react";
import { BRAND_NAME, BRAND_TAGLINE } from "@/lib/brand";

export default function ForgotPassword() {
  const { t } = useI18n();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await forgotPassword(email);
      setSent(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("auth.toast_generic_error"));
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2" data-testid="forgot-password-page">
      <div className="hidden lg:flex flex-col justify-between bg-[#064E3B] text-[#FDFBF7] p-12 relative overflow-hidden">
        <div className="absolute inset-0 pattern-islamic opacity-30" />
        <Link to="/" className="relative flex items-center gap-2.5 z-10">
          <img src="/Hikma-Verse-Logo.png" alt="Hikmah Verses" className="w-9 h-9 rounded-xl object-contain" />
          <div className="leading-tight">
            <div className="font-heading font-semibold text-sm" dir="ltr">{BRAND_NAME}</div>
            <div className="text-[10px] uppercase tracking-[0.18em] opacity-70" dir="ltr">{BRAND_TAGLINE}</div>
          </div>
        </Link>
        <div className="relative z-10">
          <div className="font-mushaf-prose text-4xl">وَمَن يَتَوَكَّلْ عَلَى ٱللَّهِ فَهُوَ حَسْبُهُۥٓ</div>
          <div className="mt-6 max-w-md text-[#FDFBF7]/75 text-sm italic">{t("auth.forgot_verse_translation")}</div>
        </div>
        <div className="relative z-10 text-xs uppercase tracking-[0.22em] opacity-60">{t("auth.hero_footer")}</div>
      </div>

      <div className="flex flex-col items-center justify-center px-5 sm:px-10 py-12">
        <div className="w-full max-w-[448px]">
          {!sent ? (
            <>
              <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-[#1F2937]">{t("auth.forgot_title")}</h1>
              <p className="mt-2 text-sm text-[#4B5563]">{t("auth.forgot_subtitle")}</p>

              <form onSubmit={submit} className="mt-8 space-y-3">
                <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-white border border-[#E7E0D2] focus-within:border-[#064E3B] transition">
                  <Mail className="w-4 h-4 text-[#4B5563]" />
                  <input data-testid="forgot-input-email" required type="email" placeholder={t("auth.email_placeholder")}
                    value={email} onChange={(e) => setEmail(e.target.value)}
                    className="w-full bg-transparent outline-none text-sm" />
                </div>
                <button data-testid="forgot-submit-btn" type="submit" disabled={loading}
                  className="w-full flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-[#064E3B] hover:bg-[#047857] text-white font-medium transition disabled:opacity-50">
                  {loading ? t("auth.sending") : t("auth.send_reset_link")} {!loading && <ArrowRight className="w-4 h-4" />}
                </button>
              </form>
            </>
          ) : (
            <div data-testid="forgot-sent-state" className="rounded-2xl bg-[#ECFDF5] border-2 border-[#10B981] p-6">
              <CheckCircle2 className="w-10 h-10 text-[#10B981]" />
              <h2 className="font-heading text-2xl font-semibold text-[#1F2937] mt-4">{t("auth.check_inbox_title")}</h2>
              <p className="mt-2 text-sm text-[#4B5563] leading-relaxed">
                {t("auth.check_inbox_body_pre")} <b>{email}</b>{t("auth.check_inbox_body_post")}
              </p>
            </div>
          )}

          <div className="mt-6 text-sm text-[#4B5563] text-center">
            {t("auth.remembered_it")}{" "}
            <Link data-testid="back-to-login-link" to="/login" className="text-[#064E3B] font-medium hover:underline">{t("auth.back_to_signin")}</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
